import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  ArrowBack,
  Inventory2,
  Sell,
  ShoppingCart,
  PriceCheck,
  Warehouse,
  CheckCircleOutline,
  AssignmentReturn,
  TrendingUp,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
} from 'recharts';
import {
  PageHeader,
  StatCard,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  TypePill,
  TableEmptyState,
  ErrorState,
} from '../../components/common/pageKit';
import { useControlRoomColors } from '../../components/common/controlRoom';
import { useChartTokens, axisProps, gridProps, tooltipStyle } from '../../dashboard/chartTokens';
import { formatCurrency, formatIndianNumber } from '../../utils/currency';
import { api } from '../../services/apiClient';
import {
  Tabs, Tab, Button as MButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Switch, FormControlLabel, IconButton, Chip,
} from '@mui/material';
import { Add as AddIcon, DeleteOutline } from '@mui/icons-material';
import { productService, Product, ProductVendor, ProductWarehouseSetting } from '../../services/productService';
import { inventoryService, Warehouse as WarehouseType } from '../../services/inventoryService';

// ---------------------------------------------------------------------------
// PRODUCT 360 — a single product's master row plus real, tenant-scoped
// aggregates (sales, returns, stock, open orders). Read-only; mirrors the
// detail-page idiom of SalesOrderDetailPage.tsx and the stat/chart treatment
// of the finance 360 pages. Data comes from GET /product-analytics/:id/360.
// ---------------------------------------------------------------------------

interface ProductMaster {
  id: number;
  code: string;
  name: string;
  hsnCode: string | null;
  gstRate: number | null;
  unitOfMeasure: string | null;
  basePrice: number | null;
  category: string | null;
  materialType: string | null;
  isStockItem: boolean;
  isActive: boolean;
}

interface FyBucket {
  label: string;
  qty: number;
  value: number;
}

interface Product360 {
  product: ProductMaster;
  sales: {
    totalQtySold: number;
    totalValue: number;
    avgSellingPrice: number;
    currentFy: FyBucket;
    previousFy: FyBucket;
    valueBasis: string;
  };
  monthlyTrend: { month: string; qty: number; value: number }[];
  topCustomers: { customerId: number | null; customerName: string; qty: number; value: number }[];
  returns: { qty: number; value: number };
  stock: { onHand: number; committed: number; available: number };
  openOrders: { qty: number; value: number };
  generatedAt: string;
}

const fmtQty = (n: number) => formatIndianNumber(Number(n) || 0);
const monthLabel = (ym: string) => {
  // 'YYYY-MM' -> 'Mon YY'
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return ym;
  const d = new Date(y, m - 1, 1);
  return d.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
};

const Product360Page: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const c = useControlRoomColors();
  const ct = useChartTokens();

  const [data, setData] = useState<Product360 | null>(null);
  const [master, setMaster] = useState<Product | null>(null);
  const [vendors, setVendors] = useState<ProductVendor[]>([]);
  const [whSettings, setWhSettings] = useState<ProductWarehouseSetting[]>([]);
  const [allWarehouses, setAllWarehouses] = useState<WarehouseType[]>([]);
  const [allVendors, setAllVendors] = useState<Array<{ id: number; company_name: string; vendor_code?: string }>>([]);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [vendorDlg, setVendorDlg] = useState(false);
  const [vForm, setVForm] = useState<any>({ vendorId: '', vendorPartNo: '', unitPrice: '', moq: '', leadTimeDays: '', isPreferred: false });
  const [whDlg, setWhDlg] = useState(false);
  const [wForm, setWForm] = useState<any>({ warehouseId: '', minStock: '', maxStock: '', reorderLevel: '' });

  const loadTabs = useCallback(async () => {
    if (!id) return;
    try {
      const [m, v, w] = await Promise.all([
        productService.getProduct(id).then((r) => r.product).catch(() => null),
        productService.getVendors(id).then((r) => r.vendors).catch(() => []),
        productService.getWarehouseSettings(id).then((r) => r.settings).catch(() => []),
      ]);
      setMaster(m); setVendors(v); setWhSettings(w);
    } catch { /* ignore */ }
  }, [id]);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const { product360 } = await api.get<{ product360: Product360 }>(`/product-analytics/${id}/360`);
      setData(product360);
      await loadTabs();
      inventoryService.getWarehouses().then((r) => setAllWarehouses(r.warehouses || [])).catch(() => {});
      api.get<{ vendors: any[] }>('/vendors').then((r) => setAllVendors((r.vendors || []).filter((x) => x.is_active !== false))).catch(() => {});
    } catch (err: any) {
      setError(err.message || 'Failed to load product 360');
    } finally {
      setLoading(false);
    }
  }, [id, loadTabs]);

  const addVendor = async () => {
    if (!id || !vForm.vendorId) return;
    try { await productService.addVendor(id, vForm); setVendorDlg(false); setVForm({ vendorId: '', vendorPartNo: '', unitPrice: '', moq: '', leadTimeDays: '', isPreferred: false }); loadTabs(); }
    catch (e: any) { setError(e.message || 'Failed to add vendor'); }
  };
  const removeVendor = async (rowId: number) => { if (!id) return; try { await productService.removeVendor(id, rowId); loadTabs(); } catch { /* ignore */ } };
  const addWh = async () => {
    if (!id || !wForm.warehouseId) return;
    try { await productService.upsertWarehouseSetting(id, wForm); setWhDlg(false); setWForm({ warehouseId: '', minStock: '', maxStock: '', reorderLevel: '' }); loadTabs(); }
    catch (e: any) { setError(e.message || 'Failed to save warehouse setting'); }
  };

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/sales/products')} sx={{ mb: 2 }}>
          Back to Products
        </Button>
        <TableSurface>
          <ErrorState message={error || 'Product not found'} onRetry={load} />
        </TableSurface>
      </Box>
    );
  }

  const { product, sales, monthlyTrend, topCustomers, returns, stock, openOrders } = data;
  const chartData = monthlyTrend.map((m) => ({ ...m, label: monthLabel(m.month) }));
  const hasSales = sales.totalQtySold > 0 || sales.totalValue > 0;

  const subtitleBits = [
    product.category ? product.category : null,
    product.hsnCode ? `HSN ${product.hsnCode}` : null,
    product.unitOfMeasure ? `Unit ${product.unitOfMeasure}` : null,
    product.gstRate != null ? `GST ${product.gstRate}%` : null,
  ].filter(Boolean);

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/sales/products')} sx={{ mb: 1 }}>
        Back to Products
      </Button>

      <PageHeader
        title={product.name}
        qualifier={`· ${product.code}`}
        subtitle={subtitleBits.join('  ·  ')}
        actions={
          <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', flexWrap: 'wrap' }}>
            {master?.product_type && <TypePill label={String(master.product_type).replace(/_/g, ' ')} tint={c.accent} />}
            {product.materialType && <TypePill label={product.materialType} tint={c.purple} />}
            {!product.isStockItem && <TypePill label="NON-STOCK" tint={c.sub} />}
            <StatusDot
              label={product.isActive ? 'Active' : 'Inactive'}
              tone={product.isActive ? 'success' : 'neutral'}
            />
          </Box>
        }
      />

      {/* KPI row */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' },
          gap: 2,
          mb: 2.5,
        }}
      >
        <StatCard title="Total Sales Value" value={formatCurrency(sales.totalValue)} icon={<Sell />} color="#2563eb" />
        <StatCard title="Qty Sold" value={fmtQty(sales.totalQtySold)} icon={<ShoppingCart />} color="#0891b2" />
        <StatCard title="Avg Selling Price" value={formatCurrency(sales.avgSellingPrice)} icon={<PriceCheck />} color="#7c3aed" />
        <StatCard
          title="On-hand"
          value={product.isStockItem ? fmtQty(stock.onHand) : null}
          icon={<Warehouse />}
          color="#059669"
        />
        <StatCard
          title="Available"
          value={product.isStockItem ? fmtQty(stock.available) : null}
          icon={<CheckCircleOutline />}
          color="#16a34a"
        />
        <StatCard title="Returns" value={formatCurrency(returns.value)} icon={<AssignmentReturn />} color="#dc2626" />
      </Box>

      <Grid container spacing={2.5}>
        {/* Monthly sales trend */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <TrendingUp fontSize="small" /> Monthly Sales Trend
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Last 12 months · value (bars) and quantity (line), net of GST
            </Typography>
            {hasSales ? (
              <Box sx={{ height: 280, width: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData} margin={{ top: 6, right: 8, bottom: 0, left: -8 }}>
                    <CartesianGrid {...gridProps(ct)} />
                    <XAxis dataKey="label" {...axisProps(ct)} />
                    <YAxis yAxisId="value" {...axisProps(ct)} width={62} tickFormatter={(v) => formatIndianNumber(v)} />
                    <YAxis yAxisId="qty" orientation="right" {...axisProps(ct)} width={44} tickFormatter={(v) => formatIndianNumber(v)} />
                    <RTooltip
                      {...tooltipStyle(ct)}
                      formatter={(v: any, n: any) => [n === 'Quantity' ? fmtQty(Number(v)) : formatCurrency(Number(v)), n]}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar yAxisId="value" dataKey="value" name="Sales Value" fill={ct.series(0)} radius={[3, 3, 0, 0]} maxBarSize={30} />
                    <Line yAxisId="qty" type="monotone" dataKey="qty" name="Quantity" stroke={ct.series(1)} strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <TableEmptyState title="No sales in the last 12 months" hint="This product has not been invoiced yet." />
            )}
          </Paper>
        </Grid>

        {/* Financial-year split + stock panel */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', mb: 2.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Sales by Financial Year
            </Typography>
            {[sales.currentFy, sales.previousFy].map((fy, idx) => (
              <Box key={fy.label} sx={{ mb: idx === 0 ? 1.5 : 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {fy.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {fmtQty(fy.qty)} {product.unitOfMeasure || 'units'}
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: idx === 0 ? c.accent : 'text.primary' }}>
                  {formatCurrency(fy.value)}
                </Typography>
              </Box>
            ))}
          </Paper>

          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Inventory2 fontSize="small" /> Stock &amp; Availability
            </Typography>
            {product.isStockItem ? (
              <>
                {[
                  ['On-hand (all warehouses)', stock.onHand, 'text.primary'],
                  ['Committed (active reservations)', stock.committed, 'warning.main'],
                ].map(([label, val]) => (
                  <Box key={label as string} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">{label}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                      {fmtQty(val as number)}
                    </Typography>
                  </Box>
                ))}
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Available</Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: stock.available > 0 ? 'success.main' : 'error.main' }}
                  >
                    {fmtQty(stock.available)}
                  </Typography>
                </Box>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                This is a non-stock product — no inventory is tracked for it.
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Top customers */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <TableSurface>
            <Box sx={{ p: 2.5, pb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Top Customers
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Top 5 by lifetime sales value for this product
              </Typography>
            </Box>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Customer</TableCell>
                    <TableCell sx={headerCellSx} align="right">Qty</TableCell>
                    <TableCell sx={headerCellSx} align="right">Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topCustomers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} sx={{ border: 0 }}>
                        <TableEmptyState title="No customer sales yet" />
                      </TableCell>
                    </TableRow>
                  ) : (
                    topCustomers.map((cust, i) => (
                      <TableRow
                        key={`${cust.customerId ?? 'x'}-${i}`}
                        sx={{ ...bodyRowSx, cursor: cust.customerId ? 'pointer' : 'default' }}
                        hover
                        onClick={() => cust.customerId && navigate(`/sales/customers/${cust.customerId}/edit`)}
                      >
                        <TableCell sx={{ py: 1.25, fontWeight: 600 }}>{cust.customerName}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtQty(cust.qty)}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                          {formatCurrency(cust.value)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        </Grid>

        {/* Open orders + returns panel */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <ShoppingCart fontSize="small" /> Open Orders
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Open quantity</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmtQty(openOrders.qty)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Open value</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(openOrders.value)}</Typography>
            </Box>
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
              Sales orders not yet invoiced or cancelled.
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <AssignmentReturn fontSize="small" /> Returns
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Returned quantity</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmtQty(returns.qty)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Returned value</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: 'error.main' }}>
                {formatCurrency(returns.value)}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
              Excludes cancelled returns.
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Master detail tabs: Vendors / Warehouses / Tax & Accounting */}
      <Paper elevation={0} sx={{ mt: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Tab label="Vendors" />
          {master?.can_stock ? <Tab label="Warehouses" /> : null}
          <Tab label="Tax & Accounting" />
        </Tabs>

        {/* Vendors */}
        {tab === 0 && (
          <Box sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
              <MButton size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setVendorDlg(true)}>Add Vendor</MButton>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead><TableRow>{['Vendor', 'Part No', 'Price', 'MOQ', 'Lead (d)', 'Preferred', ''].map((h) => <TableCell key={h} sx={headerCellSx}>{h}</TableCell>)}</TableRow></TableHead>
                <TableBody>
                  {vendors.length === 0 ? (
                    <TableRow><TableCell colSpan={7} sx={{ border: 0 }}><TableEmptyState title="No vendors linked" hint="Add an approved vendor for this product." /></TableCell></TableRow>
                  ) : vendors.map((v) => (
                    <TableRow key={v.id} sx={bodyRowSx} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{v.vendor_name || v.vendor_id}</TableCell>
                      <TableCell>{v.vendor_part_no || '—'}</TableCell>
                      <TableCell>{v.unit_price != null ? formatCurrency(Number(v.unit_price)) : '—'}</TableCell>
                      <TableCell>{v.moq ?? '—'}</TableCell>
                      <TableCell>{v.lead_time_days ?? '—'}</TableCell>
                      <TableCell>{v.is_preferred ? <Chip size="small" color="primary" label="Preferred" /> : '—'}</TableCell>
                      <TableCell align="right"><IconButton size="small" onClick={() => removeVendor(v.id)}><DeleteOutline fontSize="small" /></IconButton></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Warehouses (only when stockable; index shifts if hidden) */}
        {master?.can_stock && tab === 1 && (
          <Box sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
              <MButton size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setWhDlg(true)}>Add / Update Warehouse</MButton>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead><TableRow>{['Warehouse', 'Min', 'Max', 'Reorder'].map((h) => <TableCell key={h} sx={headerCellSx}>{h}</TableCell>)}</TableRow></TableHead>
                <TableBody>
                  {whSettings.length === 0 ? (
                    <TableRow><TableCell colSpan={4} sx={{ border: 0 }}><TableEmptyState title="No warehouse settings" hint="Set per-warehouse min/max/reorder levels." /></TableCell></TableRow>
                  ) : whSettings.map((w) => (
                    <TableRow key={w.id} sx={bodyRowSx} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{w.warehouse_name || w.warehouse_id}</TableCell>
                      <TableCell>{w.min_stock ?? '—'}</TableCell>
                      <TableCell>{w.max_stock ?? '—'}</TableCell>
                      <TableCell>{w.reorder_level ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Tax & Accounting (last tab, index depends on whether Warehouses tab is shown) */}
        {tab === (master?.can_stock ? 2 : 1) && (
          <Box sx={{ p: 2.5 }}>
            <Grid container spacing={2}>
              {[
                ['Product Type', String(master?.product_type || '—').replace(/_/g, ' ')],
                ['HSN / SAC', master?.hsn_code || master?.sac_code || '—'],
                ['GST Rate', master ? `${master.gst_rate}%` : '—'],
                ['Reverse Charge', master?.reverse_charge ? 'Yes' : 'No'],
                ['Make / Buy', master?.make_or_buy || '—'],
                ['QC Required', master?.qc_required ? 'Yes' : 'No'],
                ['Inventory A/c', master?.inventory_account_id || 'Default'],
                ['Purchase A/c', master?.purchase_account_id || 'Default'],
                ['Sales A/c', master?.sales_account_id || 'Default'],
                ['COGS A/c', master?.cogs_account_id || 'Default'],
                ['Expense A/c', master?.expense_account_id || 'Default'],
                ['Consumption A/c', master?.consumption_account_id || 'Default'],
              ].map(([label, val]) => (
                <Grid size={{ xs: 6, md: 3 }} key={label as string}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '.04em', fontWeight: 600 }}>{label}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{String(val)}</Typography>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Add vendor dialog */}
      <Dialog open={vendorDlg} onClose={() => setVendorDlg(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Add Vendor</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
            <TextField select size="small" label="Vendor" value={vForm.vendorId} onChange={(e) => setVForm({ ...vForm, vendorId: e.target.value })}>
              {allVendors.map((v) => <MenuItem key={v.id} value={v.id}>{v.company_name}</MenuItem>)}
            </TextField>
            <TextField size="small" label="Vendor Part No" value={vForm.vendorPartNo} onChange={(e) => setVForm({ ...vForm, vendorPartNo: e.target.value })} />
            <TextField size="small" type="number" label="Unit Price" value={vForm.unitPrice} onChange={(e) => setVForm({ ...vForm, unitPrice: e.target.value })} />
            <TextField size="small" type="number" label="MOQ" value={vForm.moq} onChange={(e) => setVForm({ ...vForm, moq: e.target.value })} />
            <TextField size="small" type="number" label="Lead Time (days)" value={vForm.leadTimeDays} onChange={(e) => setVForm({ ...vForm, leadTimeDays: e.target.value })} />
            <FormControlLabel control={<Switch checked={vForm.isPreferred} onChange={(e) => setVForm({ ...vForm, isPreferred: e.target.checked })} />} label="Preferred vendor" />
          </Box>
        </DialogContent>
        <DialogActions><MButton onClick={() => setVendorDlg(false)}>Cancel</MButton><MButton variant="contained" onClick={addVendor}>Add</MButton></DialogActions>
      </Dialog>

      {/* Add warehouse setting dialog */}
      <Dialog open={whDlg} onClose={() => setWhDlg(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Warehouse Settings</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
            <TextField select size="small" label="Warehouse" value={wForm.warehouseId} onChange={(e) => setWForm({ ...wForm, warehouseId: e.target.value })}>
              {allWarehouses.map((w) => <MenuItem key={w.id} value={w.id}>{w.warehouseName}</MenuItem>)}
            </TextField>
            <TextField size="small" type="number" label="Min Stock" value={wForm.minStock} onChange={(e) => setWForm({ ...wForm, minStock: e.target.value })} />
            <TextField size="small" type="number" label="Max Stock" value={wForm.maxStock} onChange={(e) => setWForm({ ...wForm, maxStock: e.target.value })} />
            <TextField size="small" type="number" label="Reorder Level" value={wForm.reorderLevel} onChange={(e) => setWForm({ ...wForm, reorderLevel: e.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions><MButton onClick={() => setWhDlg(false)}>Cancel</MButton><MButton variant="contained" onClick={addWh}>Save</MButton></DialogActions>
      </Dialog>
    </Box>
  );
};

export default Product360Page;
